<template>
  <div class="delivery-root">
    <div class="sidebar">
        <div class="oa-filters">
          <input
            v-model="oaFilter"
            placeholder="Filter OA Number"
            class="oa-filter-input"
            type="text"
          />
          <input
            v-model="customerFilter"
            placeholder="Filter Customer Name"
            class="oa-filter-input"
            type="text"
          />
          <br>
          <div class="status-checkbox-group">
            Status:
            <label v-for="status in statusOptions" :key="status" class="checkbox-label">
              <input
                type="checkbox"
                :value="status"
                v-model="statusFilter"
              />
              {{ status }}
            </label>
          </div>
        </div>
    </div>
  <div class="body">
    <table class="bom-table" v-if="deliveryRows.length">
      <thead>
        <tr>
          <th>OA Date</th>
          <th>OA Number</th>
          <th>Part Delivery</th>
          <th>Customer Name</th>
          <th>Order Quantity</th>
          <th>Due Date</th>
          <th>Packed Date</th>
          <th>Status</th>
          <th>Notes</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, idx) in filteredOAList" :key="idx">
          <td>{{ row[0] }}</td>
          <td>{{ row[1] }}</td>
          <td>{{ row[2] }}</td>
          <td>{{ row[3] }}</td>
          <td>{{ row[4] }}</td>
          <td>{{ row[5] }}</td>
          <td>{{ row[7] }}</td>
          <td>{{ row[8] }}</td>
          <td>{{ row[11] }}</td> 
        </tr>
      </tbody>
    </table>
    <div v-else>No data found.</div>
  </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';

const backendUrl = process.env.VUE_APP_BACKEND_URL;

const deliveryRows = ref([]);
const oaFilter = ref('');
const customerFilter = ref('');
const statusFilter = ref([]);


const fetchDeliverySchedule = async () => {
  const response = await fetch(`${backendUrl}/excel/delivery-schedule`);
  const data = await response.json();
  deliveryRows.value = data.items || [];
};

const filteredOAList = computed(() =>
  deliveryRows.value.filter(row =>
    (!oaFilter.value || String(row[1]).toLowerCase().includes(oaFilter.value.toLowerCase())) &&
    (!customerFilter.value || String(row[3]).toLowerCase().includes(customerFilter.value.toLowerCase())) &&
    (!statusFilter.value.length || statusFilter.value.includes(row[8]))
  )
);

const statusOptions = computed(() => {
  const set = new Set();
  deliveryRows.value.forEach(row => {
    if (row[8]) set.add(row[8]);
  });
  return Array.from(set);
});

onMounted(async () => {
  await fetchDeliverySchedule();
  statusFilter.value = statusOptions.value.filter(s => s === 'Planning' || s === 'In Progress');
});

</script>


<style scoped>
/* Visual cue animations */
@keyframes slideInLeft {
  from { opacity: 0; transform: translateX(-40px);}
  to { opacity: 1; transform: none;}
}
@keyframes slideInUp {
  from { opacity: 0; transform: translateY(40px);}
  to { opacity: 1; transform: none;}
}
.slide-in-left { animation: slideInLeft 0.7s cubic-bezier(.4,2,.6,1) both; }
.slide-in-up { animation: slideInUp 0.7s cubic-bezier(.4,2,.6,1) both; }

/* Layout */
.delivery-root {
  display: flex;
  min-height: 100vh;
  background: linear-gradient(120deg, #e0eafc 0%, #cfdef3 100%);
}
.sidebar {
  width: 320px;
  min-height: 100vh;
  padding: 32px 18px 18px 18px;
  box-sizing: border-box;
  position: relative;
  z-index: 2;
}
.body {
  flex: 1;
  padding: 40px 36px 24px 36px;
  min-height: 100vh;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
}

/* Glassmorphism sidebar */
.glass {
  background: rgba(44, 62, 80, 0.18);
  border-radius: 18px;
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.10);
  backdrop-filter: blur(8px);
  border: 1.5px solid rgba(44, 62, 80, 0.18);
}

/* Filters */
.oa-filters {
  display: flex;
  flex-direction: column;
  gap: 1.2em;
}
.oa-filter-input {
  padding: 0.7em 1em;
  border-radius: 8px;
  border: none;
  background: #f4f8fb;
  color: #2d3436;
  font-size: 1em;
  outline: none;
  box-shadow: 0 1px 4px rgba(44,62,80,0.07);
  transition: background 0.2s;
}
.oa-filter-input:focus {
  background: #e0eafc;
}

/* Checkbox group */
.status-checkbox-group {
  margin-top: 0.5em;
  font-weight: 600;
  color: #2d3436;
  font-size: 1em;
  display: flex;
  flex-direction: column;
  gap: 0.5em;
}
.checkbox-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.7em;
  margin-top: 0.3em;
}
.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5em;
  font-weight: 500;
  font-size: 0.98em;
  cursor: pointer;
  position: relative;
}
.checkbox-label input[type="checkbox"] {
  accent-color: #4f8cff;
  width: 18px;
  height: 18px;
  margin: 0;
  cursor: pointer;
}

/* Table styling */
.table-container {
  width: 100%;
  margin-top: 18px;
  background: rgba(255,255,255,0.85);
  border-radius: 16px;
  box-shadow: 0 2px 16px rgba(44,62,80,0.10);
  padding: 18px 18px 8px 18px;
  overflow-x: auto;
}
.bom-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: transparent;
}
.bom-table th, .bom-table td {
  padding: 6px 5px;
  text-align: left;
}
.bom-table th {
  background: #4f8cff;
  color: #fff;
  font-size: 1em;
  font-weight: 700;
  letter-spacing: 0.5px;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}
.bom-table td {
  background: rgba(255,255,255,0.95);
  color: #2d3436;
  font-size: 1em;
  border-bottom: 1px solid #e0eafc;
  transition: background 0.2s;
}
.bom-table tr:last-child td {
  border-bottom: none;
}

/* Status pill styling */
.status-pill {
  display: inline-block;
  padding: 3px 14px;
  border-radius: 12px;
  font-size: 0.95em;
  font-weight: 600;
  background: #e3eafc;
  color: #4f8cff;
  text-transform: capitalize;
  letter-spacing: 0.5px;
}
.status-pill.planning { background: #fffbe6; color: #f1c40f; }
.status-pill.in-progress { background: #eafaf1; color: #27ae60; }
.status-pill.completed { background: #e8eaf6; color: #6c63ff; }
.status-pill.cancelled { background: #fdecea; color: #e74c3c; }
.status-pill.delivered { background: #eaf6f6; color: #16a085; }

/* Page title */
.page-title {
  font-size: 2.2em;
  font-weight: 800;
  color: #4f8cff;
  margin-bottom: 18px;
  letter-spacing: 1px;
  text-shadow: 0 2px 8px rgba(44,62,80,0.07);
}

/* No data */
.no-data {
  color: #e74c3c;
  font-weight: 600;
  margin-top: 24px;
  font-size: 1.2em;
  text-align: center;
}

.delivery-root {
  display: flex;
  min-height: 100vh;
  background: linear-gradient(120deg, #e0eafc 0%, #cfdef3 100%);
}
</style>